"use client";

import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
const VideoPinSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();

      mm.add(
        {
          isMobile: "(max-width: 768px)",
          isDesktop: "(min-width: 769px)",
        },
        (context) => {
          const { isMobile } = context.conditions as { isMobile: boolean };
          gsap.set(sectionRef.current, { yPercent: isMobile ? 0 : -15 });

          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: sectionRef.current,
              start: isMobile ? "top 80%" : "top 0%",
              end: isMobile ? "bottom 10%" : "100% top",
              scrub: 1.5,
              pin: !isMobile,
              pinSpacing: false,
              anticipatePin: 1,
              invalidateOnRefresh: true,
              pinType: "transform",
            },
          });

          tl.to(".video-box", {
            clipPath: "circle(100% at 50% 50%)",
            ease: "power1.inOut",
          });
        }
      );

      return () => {
        mm.revert();
      };
    },
    { scope: sectionRef }
  );

  useEffect(() => {
    const section = sectionRef.current;
    const video = videoRef.current;
    if (!section || !video) return;

    const playVideo = () => {
      video.play().catch(() => {
        // Ignore autoplay rejections.
      });
    };

    const pauseVideo = () => {
      video.pause();
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;
        if (entry.isIntersecting) {
          playVideo();
          return;
        }
        pauseVideo();
      },
      { threshold: 0.15 }
    );

    observer.observe(section);

    return () => {
      observer.disconnect();
      pauseVideo();
    };
  }, []);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.style.overflow = isModalOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isModalOpen]);

  const youtubeEmbedUrl =
    "https://www.youtube.com/embed/4xKlakYoMrM?autoplay=1&mute=1&playsinline=1&rel=0&controls=1";

  return (
    <>
      <section ref={sectionRef} className="vd-pin-section">
      <div
        style={{
          clipPath: "circle(6% at 50% 50%)", 
        }}
        className="size-full video-box relative"
      >
        <video
          ref={videoRef}
          src="https://res.cloudinary.com/ddahd2kco/video/upload/v1770473003/benefits_lvg5uq.mp4"
          playsInline
          muted
          loop
          preload="none"
          aria-hidden="true"
          tabIndex={-1}
        >
          <track kind="captions" src="/captions/blank.vtt" srcLang="en" label="English" />
        </video>


        <div className="abs-center md:scale-100 scale-200 z-20">
          <Image
            src="https://res.cloudinary.com/ddahd2kco/image/upload/v1770471987/circle-text_ubxah7.svg"
            alt=""
            aria-hidden="true"
            width={200}
            height={200}
            className="spin-circle"
          />
          <button
            type="button"
            aria-label="Play promo video"
            onClick={() => setIsModalOpen(true)}
            className="play-btn z-20 cursor-pointer"
          >
            <Image
              src="https://res.cloudinary.com/ddahd2kco/image/upload/v1770472251/play_wb0hvc.svg"
              alt=""
              aria-hidden="true"
              width={50}
              height={50}
              className="size-[3vw] ml-[.5vw]"
            />
          </button>
        </div>
      </div>
    </section>

      {isMounted && createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-hidden={!isModalOpen}
          onClick={() => setIsModalOpen(false)}
          className={`fixed inset-0 z-[1200] flex items-center justify-center bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${isModalOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`relative w-[85vw] max-w-[380px] md:max-w-[420px] aspect-[9/16] rounded-[2rem] overflow-hidden bg-black shadow-2xl transition-all duration-300 ${isModalOpen ? "translate-y-0 scale-100" : "translate-y-4 scale-95"}`}
          >
            <button
              type="button"
              aria-label="Close video"
              onClick={() => setIsModalOpen(false)}
              className="absolute top-4 right-4 z-10 flex items-center justify-center size-10 rounded-full bg-white/90 text-[#523122] hover:bg-white transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="size-5">
                <path
                  fill="currentColor"
                  d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 1 0 5.7 7.11L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.42L12 13.41l4.89 4.9a1 1 0 0 0 1.42-1.41L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4Z"
                />
              </svg>
            </button>
            {isModalOpen ? (
              <iframe
                title="Kunafa World promo"
                src={youtubeEmbedUrl}
                className="w-full h-full"
                allow="autoplay; encrypted-media; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div className="w-full h-full bg-black" />
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default VideoPinSection;
